import React, { useEffect, useState } from 'react';
import { ArrowRight, Camera, Heart, Shield, MapPin } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const HeroSection = () => {
  const [animateIn, setAnimateIn] = useState(false);
  const [activeItem, setActiveItem] = useState(0);
  const { lang } = useLanguage();
  const t = familyTranslations[lang];

  useEffect(() => {
    setAnimateIn(true);
    const interval = setInterval(() => {
      setActiveItem((prev) => (prev + 1) % t.servicePreviewItems.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [t.servicePreviewItems.length]);

  const handleNavClick = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center pt-[72px] overflow-hidden">
      {/* HD Background Video - Optimized loading */}
      <video
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        onError={(e) => console.error('Video error:', e)}
        onLoadedData={() => console.log('Video loaded successfully')}
        poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect fill='%23f3f4f6' width='1920' height='1080'/%3E%3C/svg%3E"
      >
        <source
          src="/gravoom_hero.mp4"
          type="video/mp4"
        />
        Your browser does not support the video tag.
      </video>
      
      {/* Dark overlay for text readability */}
      <div className="absolute inset-0 bg-black/40 pointer-events-none" />
      
      {/* Gradient overlay (optional - for smoother blend) */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-white/80 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="flex flex-col items-center text-center pt-16 md:pt-20">
          {/* Badge */}
          <div
            className={`inline-flex items-center px-4 py-1.5 rounded-full border border-gray-200 bg-white text-sm text-gray-600 font-medium mb-8 transition-all duration-700 ${
              animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <MapPin className="w-3.5 h-3.5 mr-2 text-[#5B4FE9]" />
            {t.heroData.badge}
          </div>

          {/* Title */}
          <h1
            className={`text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight max-w-4xl transition-all duration-700 delay-100 ${
              animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.heroData.title}
            <br />
            <span className="text-[#5B4FE9]">{t.heroData.titleHighlight}</span>
            <br />
            {t.heroData.titleSuffix}
          </h1>

          {/* Subtitle */}
          <p
            className={`mt-6 text-lg md:text-xl text-gray-500 max-w-2xl leading-relaxed transition-all duration-700 delay-200 ${
              animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            {t.heroData.subtitle}
          </p>

          {/* CTAs */}
          <div
            className={`flex flex-col sm:flex-row items-center gap-4 mt-10 transition-all duration-700 delay-300 ${
              animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <button
              onClick={() => handleNavClick('#get-started')}
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#5B4FE9] text-white text-base font-semibold rounded-full hover:bg-[#4A3ED8] transition-colors duration-200 shadow-lg shadow-indigo-200/50"
            >
              {t.heroData.ctaPrimary}
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleNavClick('#how-it-works')}
              className="inline-flex items-center gap-2 text-base font-semibold text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              {t.heroData.ctaSecondary}
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Service Preview Card */}
          <div
            className={`mt-16 mb-12 w-full max-w-3xl transition-all duration-1000 delay-500 ${
              animateIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 overflow-hidden">
              {/* Top bar */}
              <div className="flex items-center gap-2 px-5 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
                <span className="ml-4 text-xs text-gray-400 font-medium">beyondmemoria.com/dashboard</span>
              </div>

              {/* Service Grid */}
              <div className="p-6">
                <div className="grid grid-cols-3 gap-3">
                  {t.servicePreviewItems.map((item, i) => (
                    <div
                      key={item}
                      className={`relative p-4 rounded-xl border transition-all duration-500 ${
                        i === activeItem
                          ? 'border-[#5B4FE9]/30 bg-indigo-50/50 shadow-sm'
                          : 'border-gray-100 bg-white'
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-800">{item}</p>
                      <div className="mt-2 space-y-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            i === activeItem ? 'bg-[#5B4FE9]/30 w-full' : 'bg-gray-100 w-3/4'
                          }`}
                        />
                        <div
                          className={`h-1.5 rounded-full transition-all duration-500 ${
                            i === activeItem ? 'bg-[#5B4FE9]/20 w-2/3' : 'bg-gray-100 w-1/2'
                          }`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                {/* Floating badge */}
                <div className="flex justify-end mt-3">
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#5B4FE9] text-white text-xs font-semibold rounded-full shadow-lg">
                    <Camera className="w-3 h-3" />
                    {t.photoVerified}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
